import { accelerometer } from './MagicBallConfig';

const GRAVITY = 9.8;
const SHAKE_COOLDOWN = 1000;

export class DeviceMotion {
  private _tiltX = 0;
  private _tiltY = 0;
  private rawTiltX = 0;
  private rawTiltY = 0;
  private shakeTimestamps: number[] = [];
  private lastShakeTime = 0;
  private _onShake: (() => void) | null = null;
  private listening = false;
  private _permissionGranted = false;
  private motionHandler: ((e: DeviceMotionEvent) => void) | null = null;

  get isSupported(): boolean {
    return 'DeviceMotionEvent' in window;
  }

  get needsPermission(): boolean {
    if (!this.isSupported) return false;
    const dme = (window as unknown as { DeviceMotionEvent?: { requestPermission?: () => Promise<string> } }).DeviceMotionEvent;
    return typeof dme?.requestPermission === 'function';
  }

  get permissionGranted(): boolean {
    return this._permissionGranted;
  }

  get tiltX(): number {
    return this._tiltX;
  }

  get tiltY(): number {
    return this._tiltY;
  }

  set onShake(cb: (() => void) | null) {
    this._onShake = cb;
  }

  async requestPermission(): Promise<boolean> {
    if (!this.needsPermission) {
      this._permissionGranted = true;
      this.start();
      return true;
    }

    try {
      const dme = (window as unknown as { DeviceMotionEvent?: { requestPermission?: () => Promise<string> } }).DeviceMotionEvent;
      if (!dme?.requestPermission) return false;
      const result = await dme.requestPermission();
      if (result === 'granted') {
        this._permissionGranted = true;
        this.start();
        return true;
      }
    } catch {
      // permission denied or error
    }
    return false;
  }

  start(): void {
    if (this.listening) return;
    this.motionHandler = (e: DeviceMotionEvent) => this.handleMotion(e);
    window.addEventListener('devicemotion', this.motionHandler);
    this.listening = true;
  }

  stop(): void {
    if (this.motionHandler) {
      window.removeEventListener('devicemotion', this.motionHandler);
      this.motionHandler = null;
    }
    this.listening = false;
  }

  private _onSimulateShake: (() => void) | null = null;

  set onSimulateShake(cb: (() => void) | null) {
    this._onSimulateShake = cb;
  }

  simulateShake(): void {
    if (this._onSimulateShake) {
      this._onSimulateShake();
    } else {
      this._onShake?.();
    }
  }

  update(delta: number): void {
    const t = Math.min(1, accelerometer.tiltSmoothing * delta);
    this._tiltX += (this.rawTiltX - this._tiltX) * t;
    this._tiltY += (this.rawTiltY - this._tiltY) * t;
  }

  private handleMotion(e: DeviceMotionEvent): void {
    const ag = e.accelerationIncludingGravity;
    if (!ag || ag.x == null || ag.y == null || ag.z == null) return;

    const ax = ag.x;
    const ay = ag.y;
    const az = ag.z;

    this.rawTiltX = Math.max(-1, Math.min(1, ax / GRAVITY));
    this.rawTiltY = Math.max(-1, Math.min(1, ay / GRAVITY));

    const magnitude = Math.sqrt(ax * ax + ay * ay + az * az);
    const netAccel = Math.abs(magnitude - GRAVITY);

    if (netAccel > accelerometer.shakeThreshold) {
      const now = Date.now();
      this.shakeTimestamps.push(now);
      this.shakeTimestamps = this.shakeTimestamps.filter(
        (ts) => now - ts < accelerometer.shakeWindow,
      );

      if (
        this.shakeTimestamps.length >= accelerometer.shakeCount &&
        now - this.lastShakeTime > SHAKE_COOLDOWN
      ) {
        this.lastShakeTime = now;
        this.shakeTimestamps = [];
        this._onShake?.();
      }
    }
  }

  dispose(): void {
    this.stop();
  }
}
